import { useState } from "react";
import { requestShippingQuotes, zoneLabel, formatEta, type RateQuote } from "@/lib/shipping";
import { RATE_SOURCE_LABEL, carrierLabel } from "@/lib/carriers";
import { money } from "@/lib/format";

interface RatePanelProps {
  shipmentId: string;
  originLabel?: string;
  onSelect: (quote: RateQuote, signature: string) => void;
  busy: boolean;
}

export function RatePanel({ shipmentId, originLabel, onSelect, busy }: RatePanelProps) {
  const [quotes, setQuotes] = useState<RateQuote[] | null>(null);
  const [signature, setSignature] = useState("");
  const [liveAccounts, setLiveAccounts] = useState<{ id: string; carrier: string; name: string; auth_status: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const getQuotes = async () => {
    setError("");
    setLoading(true);
    try {
      const result = await requestShippingQuotes(shipmentId);
      if (!result.ok) {
        setError(result.error ?? "Couldn't rate this shipment.");
        setQuotes(null);
        return;
      }
      setQuotes(result.quotes ?? []);
      setSignature(result.signature ?? "");
      setLiveAccounts(result.live_accounts ?? []);
      if ((result.quotes ?? []).length === 0) {
        setError("No enabled carrier services cover this destination. Add carrier services or connect a carrier account.");
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't fetch rates.");
      setQuotes(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-800">Shipping options</h3>
        {originLabel && <span className="text-xs text-slate-400">{originLabel}</span>}
      </div>

      <button
        onClick={getQuotes}
        disabled={loading || busy}
        className="w-full px-4 py-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap flex items-center justify-center gap-2"
      >
        <i className="ri-flashlight-line" />
        {loading ? "Calculating options..." : "Get shipping options"}
      </button>

      {error && <div className="mt-3 px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{error}</div>}

      {quotes && quotes.length > 0 && (
        <div className="mt-4 space-y-2">
          <div className="flex items-center justify-between text-xs text-slate-400">
            <span>Ranked by price, then speed</span>
            <span>{quotes[0] ? `Chargeable ${quotes[0].chargeable_weight_kg} kg` : ""}</span>
          </div>
          {quotes.map((q, i) => (
            <div
              key={q.carrier_service_id}
              className={`border rounded-lg p-3 ${selectedId === q.carrier_service_id ? "border-emerald-500 bg-emerald-50/40" : "border-slate-100"}`}
            >
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-slate-100 text-slate-600 text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-slate-800">{q.service_name}</p>
                    <span className="text-[10px] font-bold uppercase bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">{q.carrier_code}</span>
                    <span className="text-[10px] bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded">{zoneLabel(q.zone)}</span>
                    <span
                      className={`text-[10px] px-1.5 py-0.5 rounded ${
                        q.rate_source === "live" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"
                      }`}
                    >
                      {RATE_SOURCE_LABEL[q.rate_source ?? "internal"] ?? "Internal estimate"}
                    </span>
                  </div>
                  {q.carrier_account_name && <p className="text-xs text-slate-400 mt-0.5">{q.carrier_account_name}</p>}
                  <p className="text-xs text-slate-400 mt-0.5">
                    {carrierLabel(q.carrier_code)} · {formatEta(q.eta_min_days, q.eta_max_days)} delivery
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-bold text-slate-900">{q.currency} {money(q.cost, q.currency)}</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setSelectedId(q.carrier_service_id);
                  onSelect(q, signature);
                }}
                disabled={busy}
                className="mt-2 w-full px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-xs font-medium rounded-lg whitespace-nowrap"
              >
                {selectedId === q.carrier_service_id ? "Selected" : "Select this service"}
              </button>
            </div>
          ))}
        </div>
      )}

      {liveAccounts.length > 0 && (
        <div className="mt-4 px-3 py-2.5 bg-slate-50 border border-slate-100 rounded-lg">
          <p className="text-xs font-medium text-slate-600 mb-1">Connected carrier accounts</p>
          {liveAccounts.map((a) => (
            <p key={a.id} className="text-xs text-slate-400">
              {a.name} · live rates fetched via carrier adapter
            </p>
          ))}
        </div>
      )}
    </div>
  );
}